import {
  ChangeEventHandler,
  ComponentProps,
  FC,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './styles.module.css';
import { FormProvider, useForm } from 'react-hook-form';
import Select, { Option } from '../../../../components/common/Select';
import { required } from '../../../../message';
import { logger } from '../../../../services/logger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import FormItem from '../../../../components/common/FormItem';
import Input from '../../../../components/common/Input';
import classNames from 'classnames';
import Button from '../../../../components/common/Button';
import { putDriverRequestBody } from '../../../../types/driver';
import {
  useGetDriverLicensesQuery,
  useGetDriverSkillLevelsQuery,
} from '../../../../api/driverApi';
import { useGetCarQualificationsQuery } from '../../../../api/carApi';
import Label from '../../../../components/common/Label';
import CheckBoxs from '../CheckBoxs';

export type DriverFormDataProviderProps = {
  formData: putDriverRequestBody;
  onSubmit: (data: putDriverRequestBody) => void;
};
const context = createContext<DriverFormDataProviderProps | null>(null);
export const DriverFormDataProvider = context.Provider;

const EditSideBar: FC = () => {
  const driverFormDataProps = useContext(context);
  if (!driverFormDataProps) {
    return <></>;
  }
  return <EditDriverForm {...driverFormDataProps} />;
};

export type ValueObj = {
  name?: string;
  label: string;
  value: string;
  checked: boolean;
};

export type CheckBoxGroupOnChangeProps = {
  values: ValueObj[];
  selectedValues: string[];
};

const labels: Record<keyof putDriverRequestBody, string> = {
  driverName: '名前',
  skillLevelId: '習熟度',
  driverLicenseId: '免許種別',
  carQualificationIds: '資格',
};
const setValueOpt = {
  shouldTouch: true,
  shouldDirty: true,
  shouldValidate: true,
};
type TypedFormItemProps = ComponentProps<typeof FormItem<putDriverRequestBody>>;
const TypedFormItem = (props: TypedFormItemProps) => (
  <div className={styles.formItem}>
    <FormItem<putDriverRequestBody> {...props} />
  </div>
);

const EditDriverForm = ({
  formData,
  onSubmit,
}: DriverFormDataProviderProps) => {
  const form = useForm({
    defaultValues: formData,
    mode: 'onBlur',
  });
  const { register, setValue, watch, handleSubmit, getFieldState } = form;

  useEffect(() => {
    form.reset(formData);
  }, [formData]);
  const submitHandler = handleSubmit(onSubmit, (e) => logger.error(e));

  const [isOpened, setIsOpened] = useState(false);
  useEffect(() => {
    setIsOpened(true);
  }, [formData]);
  const closeForm = () => setIsOpened(false);

  // 習熟度ハンドラセットアップ
  const { data: skillLevelTypes = [] } = useGetDriverSkillLevelsQuery();
  const skillLevelTypesOptions: Option[] = skillLevelTypes.map((ct) => ({
    label: ct.skillLevel,
    value: `${ct.skillLevelId}`,
  }));
  const onChangeSkillLevelType: ChangeEventHandler<HTMLSelectElement> = (e) => {
    setValue('skillLevelId', Number(e.target.value), setValueOpt);
  };
  register('skillLevelId');

  // 免許種別ハンドラセットアップ
  const { data: licenseTypes = [] } = useGetDriverLicensesQuery();
  const LicenseTypesOptions: Option[] = licenseTypes.map((p) => ({
    label: p.driverLicense,
    value: `${p.driverLicenseId}`,
  }));
  const onChangeLicenseTypes: ChangeEventHandler<HTMLSelectElement> = (e) => {
    setValue('driverLicenseId', Number(e.target.value), setValueOpt);
  };
  register('driverLicenseId');

  // 資格種別ハンドラセットアップ
  const { data: carQualificationTypes = [] } = useGetCarQualificationsQuery();
  const [
    carQualificationCheckBoxGroupValues,
    updateCarQualificationCheckBoxGroupValues,
  ] = useState<ValueObj[]>([]);
  const carQualificationIds = form.getValues('carQualificationIds');
  useEffect(() => {
    updateCarQualificationCheckBoxGroupValues(
      (carQualificationTypes || []).map((item) => {
        const isIncludeData = carQualificationIds.includes(
          item.carQualificationId
        );
        return {
          value: `${item.carQualificationId}`,
          label: item.carQualification,
          checked: isIncludeData,
        };
      })
    );
  }, [carQualificationTypes, carQualificationIds]);

  const onChangeWasteItem = ({
    values,
    selectedValues,
  }: CheckBoxGroupOnChangeProps) => {
    updateCarQualificationCheckBoxGroupValues(values);
    setValue(
      'carQualificationIds',
      selectedValues.map((v) => Number(v)),
      setValueOpt
    );
  };

  const getVariant = (key: keyof putDriverRequestBody) =>
    getFieldState(key)?.error
      ? ['fit' as const, 'error' as const]
      : ['fit' as const];

  const formRef = useRef<HTMLFormElement | null>(null);

  return (
    <div
      className={classNames(
        styles.sidebar,
        isOpened ? styles.open : styles.close
      )}
    >
      <h4 className={styles.head}>
        {`ドライバー編集`}
        <button onClick={closeForm} className={styles.closer}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </h4>
      <FormProvider {...form}>
        <form onSubmit={submitHandler} className={styles.form} ref={formRef}>
          <div className={styles.nameInput}>
            <Label>名前</Label>
            <Input
              {...register('driverName', {
                required: required(labels.driverName),
              })}
              variants={getVariant('driverName')}
              disabled
            />
          </div>
          <TypedFormItem label={labels.skillLevelId} name="skillLevelId">
            <div className={styles.input}>
              <Select
                options={skillLevelTypesOptions}
                value={`${watch('skillLevelId') ?? ''}`}
                onChange={onChangeSkillLevelType}
                variants={getVariant('skillLevelId')}
                placeholder=""
              />
            </div>
          </TypedFormItem>
          <TypedFormItem label={labels.driverLicenseId} name="driverLicenseId">
            <div className={styles.input}>
              <Select
                options={LicenseTypesOptions}
                value={`${watch('driverLicenseId') ?? ''}`}
                onChange={onChangeLicenseTypes}
                variants={getVariant('driverLicenseId')}
                placeholder=""
              />
            </div>
          </TypedFormItem>
          <Label>資格</Label>
          <CheckBoxs
            values={carQualificationCheckBoxGroupValues}
            onChange={onChangeWasteItem}
          ></CheckBoxs>
          <div className={styles.controls}>
            <div className={styles.controlButton}>
              <Button
                type="button"
                variants={['fit']}
                onClick={() => formRef.current?.requestSubmit()}
              >
                確定
              </Button>
            </div>
            <div className={styles.controlButton}>
              <Button
                type="button"
                variants={['fit', 'cancel']}
                onClick={closeForm}
              >
                キャンセル
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default EditSideBar;
